<template>
  <div id="app" class="page" style="overflow: hidden">
    <section class="contact-hero">
      <div class="hero-content">
        <h1>{{ isEnglish ? 'Rate Board' : 'Bank View' }}</h1>
      </div>
    </section>
    <div class="content">
      <div class="controls">
        <button @click="fetchRates" class="refresh-button">{{ isEnglish ? 'Refresh Rates' : 'Actualiser les taux' }}</button>
        <button @click="toggleFullScreen" class="full-screen-button">
          {{ isFullScreen ? (isEnglish ? 'Exit Full Screen' : 'Quitter le mode plein écran') : (isEnglish ? 'Full Screen' : 'Mode plein écran') }}
        </button>
      </div>
      <p v-if="errorMessage" class="status-message error">{{ errorMessage }}</p>

      <div class="currency-module" :class="{ 'full-screen': isFullScreen }">
        <div class="board-header">
          <div>
            <h2>{{ isEnglish ? 'Exchange Rates' : 'Taux de change' }}</h2>
            <p>{{ isEnglish ? 'Buy and sell rates in CAD' : 'Achat et vente en CAD' }}</p>
          </div>
          <p v-if="lastUpdatedLabel" class="updated-at">{{ isEnglish ? 'Last updated' : 'Dernière mise à jour' }} : {{ lastUpdatedLabel }}</p>
        </div>
        <table class="currency-table">
          <thead>
            <tr>
              <th style="text-align: left;">{{ isEnglish ? 'Currency' : 'Devise' }}</th>
              <th>{{ isEnglish ? 'Buy' : 'Achat' }}</th>
              <th>{{ isEnglish ? 'Sell' : 'Vente' }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="rate in rates" :key="rate.code">
              <td style="text-align: left;">
                <CountryFlag :iso="countryCodePerCurrency[rate.code] || rate.code.slice(0, 2)" mode="squared" />
                <p style="text-align: left;" class="currency-table-text">{{ rate.code }} {{ displayCurrencyName(rate) }}</p>
              </td>
              <td class="digital-text">{{ formatRate(rate.buy) }}</td>
              <td class="digital-text">{{ formatRate(rate.sell) }}</td>
            </tr>
          </tbody>
        </table>
        <div class="ticker">
          <p>{{ tickerText }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CountryFlag from '@dzangolab/vue-country-flag-icon';
import '@dzangolab/vue-country-flag-icon/dist/CountryFlag.css';
import { fetchBoardRates } from '@/services/ratesService';

export default {
  components: { CountryFlag },
  data() {
    return {
      refreshTimer: null,
      errorMessage: '',
      isFullScreen: false,
      updatedAt: null,
      rates: [],
      countryCodePerCurrency: {
        CAD: 'CA',
        USD: 'US',
        EUR: 'EU',
        GBP: 'GB',
        XOF: 'SN',
        XAF: 'CM',
        CDF: 'CD',
        GNF: 'GN',
        NGN: 'NG',
        MAD: 'MA',
        AED: 'AE'
      },
      englishCurrencyNames: {
        USD: 'United States',
        EUR: 'Euro',
        GBP: 'British Pound',
        BIF: 'Burundi',
        RWF: 'Rwanda',
        AED: 'United Arab Emirates',
        CAD: 'Canada',
        CHF: 'Switzerland',
        CNY: 'China',
        ZAR: 'South Africa'
      }
    };
  },
  computed: {
    tickerText() {
      const codes = this.rates.map((rate) => `${rate.code}: ${this.formatRate(rate.buy)} / ${this.formatRate(rate.sell)}`);
      if (!codes.length) return this.isEnglish ? 'Rates update in progress...' : 'Mise à jour des taux en cours...';
      return `${this.isEnglish ? 'Rates update' : 'Mise à jour des taux'} - ${codes.join('   |   ')}`;
    },
    isEnglish() {
      return this.$route.path.startsWith('/en');
    },
    lastUpdatedLabel() {
      if (!this.updatedAt) return '';
      const date = new Date(this.updatedAt);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleString(this.isEnglish ? 'en-CA' : 'fr-CA', {
        dateStyle: 'medium',
        timeStyle: 'short'
      });
    }
  },
  methods: {
    formatRate(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return '-';
      return number.toFixed(number >= 100 ? 2 : 6);
    },
    displayCurrencyName(rate) {
      if (!rate) return '';
      return this.isEnglish ? (this.englishCurrencyNames[rate.code] || rate.name) : rate.name;
    },
    toggleFullScreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        this.isFullScreen = true;
      } else if (document.exitFullscreen) {
        document.exitFullscreen();
        this.isFullScreen = false;
      }
      document.body.style.overflow = this.isFullScreen ? 'hidden' : '';
    },
    async fetchRates() {
      this.errorMessage = '';
      try {
        const response = await fetchBoardRates();
        this.rates = response.rates;
        this.updatedAt = response.updated_at;
      } catch (error) {
        this.errorMessage = this.isEnglish ? 'Unable to refresh rates. The latest available data remains displayed.' : 'Impossible de rafraîchir les taux. Les dernières données restent affichées.';
        console.error(error);
      }
    }
  },
  mounted() {
    this.fetchRates();
    this.refreshTimer = setInterval(this.fetchRates, 60000);
  },
  beforeUnmount() {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
  }
};
</script>

<style scoped>
.page {
  background: #07090f;
  min-height: 100vh;
}

.controls {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 16px;
}

.refresh-button,
.full-screen-button {
  background: #c39446;
  border: 0;
  border-radius: 5px;
  color: #fff;
  cursor: pointer;
  font-weight: 700;
  padding: 10px 14px;
}

.full-screen-button {
  background: #001041;
}

.status-message.error {
  background: #fff0f0;
  border: 1px solid #e2b1b1;
  border-radius: 8px;
  color: #8b1e1e;
  padding: 12px 14px;
}

.ticker {
  position: fixed;
  bottom: calc(0% + 0px);
  left: 0;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  background-color: #001041;
  color: white;
  padding: 12px;
  border-radius: 0;
}

.ticker p {
  display: inline-block;
  padding-right: 100%;
  animation: tickerAnimation 40s linear infinite;
  font-size: clamp(1rem, 2vw, 1.45rem);
  margin: 0;
}

.board-header {
  align-items: center;
  background: linear-gradient(90deg, #001041, #0f1d3a);
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  color: #fff;
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding: 20px 28px;
}

.board-header h2,
.board-header p {
  margin: 0;
}

.board-header h2 {
  font-size: clamp(2rem, 4vw, 3.6rem);
  line-height: 1;
}

.updated-at {
  color: #d9c28a;
  font-size: clamp(0.95rem, 1.5vw, 1.25rem);
  white-space: nowrap;
}

@keyframes tickerAnimation {
  0% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(-100%);
  }
}
.bank-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  background-color: #f4f7fb;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  height: 100vh;
  transition: all 0.3s ease;
  box-sizing: border-box;
  font-size:36px;
}

.full-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #111;
  color: #fff;
  padding: 0;
  z-index: 9999;
  overflow: hidden;
}
@font-face {
  font-family: 'Digital-7';
  src: url('@/assets/fonts/digital-7.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
}

.digital-text {
  font-family: 'Digital-7', sans-serif;
  color: #ff3b30;
  font-size: clamp(2.2rem, 5vw, 4.8rem);
  font-variant-numeric: tabular-nums;
  line-height: 1;
  text-shadow: 0 0 10px rgba(255, 59, 48, 0.2);
}
.currency-table-text {
  color: #fff!important;
  display: inline-block;
  font-size: clamp(1.1rem, 2.2vw, 2rem);
  font-weight: 700;
  margin: 0 0 0 18px;
  vertical-align: middle;
}
.currency-table {
  margin: 0px auto!important;
  border: none;
  border-spacing: 0;
  padding: 10px;
  width: 100%;
  max-width: 100%!important;
  border: 1px solid #001041;
  padding: 0;
  background: #10131a;
}
.currency-table thead {
 background: #111;
 color: #fff; 
 padding: 0;
}
.currency-table th {
  color: #d9c28a;
  font-size: clamp(0.85rem, 1.3vw, 1rem);
  letter-spacing: 0;
  padding: 16px 24px;
  text-transform: uppercase;
}
.flag-icon {
  width: clamp(54px, 7vw, 96px)!important;
  height: clamp(36px, 4.8vw, 64px)!important;
  float: none;
  vertical-align: middle;
}
.currency-table td {
  text-align: center;
  max-width: 50%;
}
.currency-table td {
  text-align: center;
}
  /* Hero Section */
  .contact-hero {
    background: linear-gradient(to right, #45a5f6, #007acc);
    background: 
    linear-gradient(to right, rgba(69, 166, 246, 0.719), rgba(0, 122, 204, 0.658)), 
    url('@/assets/taux.jpg');
    background-size: cover;
    background-position: top center;
    color: white;
    text-align: center;
    padding: 80px 20px;
    animation: fadeIn 1.5s ease-in-out;
    min-height: 00px;
  }

  .contact-hero .hero-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 60px;
  }

  .contact-hero h1 {
    font-size: 2.5rem;
    margin-bottom: 10px;
  }

  .contact-hero p {
    font-size: 1.2rem;
    margin-top: 10px;
  }

  .content {
    max-width: 1200px;
    margin: 28px auto 80px;
    padding: 0 20px;
  }
  .taux-de-change {
    margin-top: 100px;
  }
  .currency-table {
    margin: 40px auto;
    border: none;
    border-spacing: 0;
    padding: 10px;
    width: 100%;
    max-width: 900px;
    border: 0px solid #001041;
    padding: 0;
  }
  .currency-table td {
    border: 1px solid #555;

  }
  .currency-table thead {
  background: #001041;
  color: #fff; 
  padding: 0;
  }
  .currency-table th {
    padding: 15px;
  }
  .currency-table td {
    text-align: center;
    padding: 18px 24px;
    
  }
  .price-up {
    background-color: #d4f8d4;
  }
  .price-down {
    background-color: #f8d4d4;
  }
  .price-stable {
    background-color: #111;
  }
  /* Animations */
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .small-print {
    font-size:12px;
    padding: 0;
    margin: 10px 10px;;
    text-align:right;
    color: #777;
  }
  /* Mobile Styles */
@media (max-width: 768px) {
  .controls,
  .board-header {
    align-items: stretch;
    flex-direction: column;
  }
  .currency-table {
    font-size: 14px;
  }
  .currency-table td {
    border: 1px solid #313131;

  }
  .flag-icon {
      width: 42px !important;
      height: 35px !important;
      margin-right: 10px;
  }
  .exchange-rate {
    padding: 0;
  }
  .content {
    padding: 0 10px;
  }
  .currency-table th,
  .currency-table td {
    padding: 12px 8px;
  }
}

</style>
